/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Prompt Template Runner & Dynamic Schema Studio View (Task 10.2.6 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef } from 'react';
import {
  ArrowLeft,
  Heart,
} from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import type { EnrichedPromptTemplate } from '../services/prompt-catalog.service';
import {
  DynamicTemplateForm,
  type DynamicFormConfig,
} from '../components/templates/DynamicTemplateForm';
import { TemplateOutputPanel } from '../components/templates/TemplateOutputPanel';
import { PromptExecutionService } from '../services/prompt-execution.service';
import { usePromptTemplateStore } from '../stores/usePromptTemplateStore';
import { useI18n } from '../stores/useI18nStore';

interface PromptTemplateRunnerViewProps {
  template: EnrichedPromptTemplate;
  onBack: () => void;
  onNavigateToEditor?: () => void;
}

/**
 * Returns dynamic category color styles and standardized Sophia icons.
 */
function getCategoryBadge(categoryId: string): { bg: string; icon: SophiaDefaultIconName } {
  switch (categoryId) {
    case 'pcat_blog':
      return { bg: 'bg-primary/10 text-primary border-primary/20', icon: 'brainstorm' };
    case 'pcat_marketing':
      return { bg: 'bg-accent/10 text-accent border-accent/20', icon: 'strategy' };
    case 'pcat_email':
      return { bg: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', icon: 'virtual-call' };
    case 'pcat_ecommerce':
      return { bg: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: 'digital-wallet' };
    case 'pcat_coding':
      return { bg: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20', icon: 'binary-code' };
    default:
      return { bg: 'bg-primary/10 text-primary border-primary/20', icon: 'solution' };
  }
}

export const PromptTemplateRunnerView: React.FC<PromptTemplateRunnerViewProps> = ({
  template,
  onBack,
  onNavigateToEditor,
}) => {
  const { isFavorite, toggleFavorite } = usePromptTemplateStore();
  const { t } = useI18n();
  const favorited = isFavorite(template.slug);

  const categoryBadge = getCategoryBadge(template.categoryId);

  // Stream state
  const [isStreaming, setIsStreaming] = useState(false);
  const [outputContent, setOutputContent] = useState('');
  const [tokensGenerated, setTokensGenerated] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Handle Form Submission -> Trigger Stream
  const handleFormSubmit = async (
    formValues: Record<string, unknown>,
    config: DynamicFormConfig
  ) => {
    // Cancel any previous stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsStreaming(true);
    setOutputContent('');
    setTokensGenerated(0);

    try {
      await PromptExecutionService.executePromptStream(
        template,
        formValues,
        {
          onToken: (_token, accumulated) => {
            setOutputContent(accumulated);
            const wordCount = accumulated.split(/\s+/).filter(Boolean).length;
            setTokensGenerated(Math.ceil(wordCount * 1.33));
          },
          onComplete: (fullText, meta) => {
            setOutputContent(fullText);
            setTokensGenerated(meta.tokens);
            setIsStreaming(false);
          },
          onError: (err) => {
            if (err.name !== 'AbortError') {
              setOutputContent((prev) => `${prev}\n\n⚠️ Error: ${err.message}`);
            }
            setIsStreaming(false);
          },
        },
        {
          model: config.model,
          temperature: config.temperature,
          outputLanguage: config.outputLanguage,
          outputLength: config.outputLength,
          maxTokens: config.maxTokens,
          toneOfVoice: config.toneOfVoice,
          focusKeywords: config.focusKeywords,
          signal: abortController.signal,
          chunkDelayMs: 20,
        }
      );
    } catch {
      setIsStreaming(false);
    }
  };

  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  };

  return (
    <div className="space-y-5">
      {/* 1. Header Navigation & Template Info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl border border-surface-border bg-surface shadow-glass">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-xl border border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors"
            title={t('common.back')}
            aria-label={t('common.back')}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center border shadow-inner ${categoryBadge.bg}`}
          >
            <SophiaIcon name={categoryBadge.icon} size={20} className="shrink-0" />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-heading font-bold text-foreground">
                {template.title}
              </h2>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${categoryBadge.bg}`}
              >
                {template.category.name}
              </span>
            </div>
            <p className="text-xs text-foreground-muted line-clamp-1 mt-0.5 max-w-xl">
              {template.description}
            </p>
          </div>
        </div>

        {/* Right Badges */}
        <div className="flex items-center gap-2 self-end sm:self-center">
          <span className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold px-2.5 py-1 rounded-xl bg-amber-500/10 text-amber-500 border border-amber-500/20">
            <SophiaIcon name="battery" size={13} className="text-amber-500 shrink-0" />
            {template.creditsCost} {t('common.credits')}
          </span>

          <button
            type="button"
            onClick={() => toggleFavorite(template.slug)}
            className={`p-2 rounded-xl border transition-all ${
              favorited
                ? 'bg-rose-500/10 border-rose-500/30 text-rose-500'
                : 'border-surface-border text-foreground-muted hover:text-rose-500 hover:bg-rose-500/10'
            }`}
            title={favorited ? 'Remove favorite' : 'Save favorite'}
          >
            <Heart className={`w-4 h-4 ${favorited ? 'fill-rose-500' : ''}`} />
          </button>
        </div>
      </div>

      {/* 2. Dual-Column Studio Workspace: Form (Left) vs Output (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Left Column: Dynamic Schema Form (5 cols) */}
        <div className="lg:col-span-5 p-5 sm:p-6 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-4">
          <div className="flex items-center justify-between border-b border-surface-border/60 pb-3">
            <div className="flex items-center gap-2">
              <SophiaIcon name="filter" size={16} className="text-primary shrink-0" />
              <h3 className="text-sm font-heading font-bold text-foreground">
                {t('templates.inputsTitle')}
              </h3>
            </div>
            <span className="text-[11px] font-mono text-foreground-muted">
              {template.inputSchema.length} fields
            </span>
          </div>

          <DynamicTemplateForm
            template={template}
            isGenerating={isStreaming}
            onSubmit={handleFormSubmit}
          />
        </div>

        {/* Right Column: Live Output Panel & Document Actions (7 cols) */}
        <div className="lg:col-span-7 h-[680px]">
          <TemplateOutputPanel
            content={outputContent}
            isStreaming={isStreaming}
            tokensGenerated={tokensGenerated}
            templateTitle={template.title}
            templateId={template.id}
            onStopStreaming={handleStopStreaming}
            onNavigateToEditor={onNavigateToEditor}
            onClear={() => {
              setOutputContent('');
              setTokensGenerated(0);
            }}
          />
        </div>
      </div>
    </div>
  );
};
